import axios from 'axios'
import { MessageBox, Message, Notification } from 'element-ui'
import store from '@/store'
import router from '@/router'
import { getToken } from '@/utils/auth'

// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_API || '/api',
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8'
  }
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    
    // 添加语言字段到请求头
    const language = localStorage.getItem('language') || 'zh_cn'
    config.headers['lang'] = language
    
    // 添加认证字段
    const token = getToken()
    if (token) {
      // 获取tokenHead和tokenHeader
      const tokenHead = localStorage.getItem('tokenHead') || 'Bearer'
      const tokenHeader = localStorage.getItem('tokenHeader') || 'Authorization'
      
      // 使用后端返回的tokenHeader和tokenHead构建请求头
      config.headers[tokenHeader] = `${tokenHead} ${token}`
    }
    
    return config
  },
  error => {
    console.error('请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 文件下载直接返回
    if (response.config && response.config.responseType === 'blob') {
      return response
    }

    const res = response.data

    // 处理mock数据或普通API响应
    if (!res) {
      const error = new Error('返回数据为空')
      console.error('响应错误:', error)
      return Promise.reject(error)
    }

    // 统一处理响应状态码
    // 支持特殊页面处理：如果请求配置中包含 skipGlobalErrorHandler: true，则跳过全局错误处理
    const skipGlobalHandler = response.config.skipGlobalErrorHandler
    
    if (res.code === 200) {
      // 请求成功
      return res
    } else if (res.code === 0) {
      // 兼容旧版本，code为0也表示成功
      return res
    } else {
      const errorMessage = res.message || '请求失败'

      // 如果特殊页面需要自己处理错误，则不显示全局错误提示
      if (!skipGlobalHandler) {
        // 根据不同状态码显示不同的错误信息
        switch (res.code) {
          case 401:
            // 认证失败，需要重新登录
            // 如果当前已经在登录页面，则不显示弹窗
            if (router.currentRoute.path !== '/login') {
              MessageBox.confirm('您的登录已过期，请重新登录', '认证失败', {
                confirmButtonText: '重新登录',
                cancelButtonText: '取消',
                type: 'warning'
              }).then(() => {
                store.dispatch('user/resetToken').then(() => {
                  router.push('/login')
                })
              }).catch(() => {
                // 用户点击取消按钮，也需要清除token并跳转到登录页
                store.dispatch('user/resetToken').then(() => {
                  router.push('/login')
                })
              })
            } else {
              // 如果已经在登录页面，只清除token即可
              store.dispatch('user/resetToken')
            }
            break
          case 403:
            // 权限不足
            Message({
              message: errorMessage || '权限不足，无法访问该资源',
              type: 'error',
              duration: 5000
            })
            break
          case 404:
            // 资源不存在
            Message({
              message: errorMessage || '请求的资源不存在',
              type: 'error',
              duration: 5000
            })
            break
          case 500:
            // 服务器错误
            Message({
              message: errorMessage || '服务器内部错误，请稍后重试',
              type: 'error',
              duration: 5000
            })
            break
          default:
            // 其他错误
            Message({
              message: errorMessage,
              type: 'error',
              duration: 5000
            })
        }
      }
      
      return Promise.reject(new Error(res.message || '请求失败'))
    }
  },
  error => {
    console.error('响应错误:', {
      message: error.message,
      code: error.code,
      config: error.config,
      response: error.response && {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      }
    })
    Message({
      message: error.message || '系统错误',
      type: 'error',
      duration: 5000
    })
    return Promise.reject(error)
  }
)

export default service
